import { Flex } from '@mantine/core';
import type { TFunction } from 'i18next';
import React from 'react';

import type { Fundraiser } from '@feathr/blackbox';
import {
  ButtonValid,
  Checkbox,
  Fieldset,
  FormElement,
  type IModalStep,
  Input,
  List,
  Radios,
  showError,
  Text,
  toast,
} from '@feathr/components';
import type { TModalSize } from '@feathr/components/src/Modal/Modal';
import { cssVar } from '@feathr/hooks';

interface IProps {
  fundraiser?: Fundraiser;
  logos: { salesforce: string; blackbaud: string };
  t: TFunction;
}

/**
 * @param fundraiser - The fundraiser model being used in the CRM connection process.
 *
 * When the back button is clicked on the ready to enable integration step set the
 * ready_to_enable_integration flag to false so that the user can re-enable it if they go forward again.
 */
function handleOnBackReadyToEnableIntegration(fundraiser: Fundraiser): void {
  const crm = fundraiser.get('crm') ?? {};
  fundraiser.set({
    crm: {
      ...crm,
      ready_to_enable_integration: false,
      credential_check_passed: false,
    },
  });
}

/**
 * Handle the "Check CRM" button click by validating the CRM credentials.
 * @param fundraiser - The fundraiser model being used in the CRM connection process.
 * @param t - The translation function.
 */
async function handleCheckCRM(fundraiser: Fundraiser, t: TFunction): Promise<void> {
  try {
    const crm = fundraiser.get('crm');
    await fundraiser?.checkCRM({ crm });
    fundraiser.set({
      crm: {
        ...crm,
        credential_check_passed: true,
      },
    });
    toast(t('CRM credentials are valid'), { type: 'success' });
  } catch (error) {
    showError(error, t);
  }
}

function getSalesforceSteps(
  baseProps: Partial<IModalStep>,
  fundraiser: Fundraiser,
  t: TFunction,
): IModalStep[] {
  return [
    {
      ...baseProps,
      title: t('Provide Salesforce Credentials'),
      description: t(
        'To connect to Salesforce, you need to provide a client ID, client secret, and domain.',
      ),
      content: (
        <Flex direction={'column'} gap={cssVar('--spacing-4')}>
          <FormElement>
            <Fieldset>
              <Input
                attribute={['crm', 'client_id']}
                helpText={t('Sometimes called "Consumer Key"')}
                isClearable={true}
                label={t('Client ID')}
                model={fundraiser}
                type={'password'}
              />
              <Input
                attribute={['crm', 'client_secret']}
                helpText={t('Sometimes called "Consumer Secret"')}
                isClearable={true}
                label={t('Client Secret')}
                model={fundraiser}
                type={'password'}
              />
              <Input
                attribute={['crm', 'domain']}
                helpText={t('The domain name of your Salesforce without the domain suffix.')}
                isClearable={true}
                label={t('Domain Name')}
                model={fundraiser}
                suffix={'.my.salesforce.com'}
              />
            </Fieldset>
          </FormElement>
          <ButtonValid
            errors={
              fundraiser.validate(['crm.client_id', 'crm.client_secret', 'crm.domain'], false)
                .errors
            }
            isFullWidth={true}
            onClick={(): Promise<void> => handleCheckCRM(fundraiser, t)}
            tooltipPosition={'bottom'}
            type={'primary'}
          >
            {t('Check user credentials')}
          </ButtonValid>
        </Flex>
      ),
      nextStepDisabled:
        fundraiser.validate(
          ['crm.client_id', 'crm.client_secret', 'crm.domain', 'crm.credential_check_passed'],
          false,
        ).errors?.length > 0,
      errors: fundraiser.validate(
        ['crm.client_id', 'crm.client_secret', 'crm.domain', 'crm.credential_check_passed'],
        false,
        'grouped',
      ).errors,
    },
    {
      ...baseProps,
      title: t('Sync Donations to Salesforce NPSP'),
      description: t(
        'Feathr creates complete donor records and donation opportunities in Salesforce automatically.',
      ),
      content: (
        <Flex direction={'column'} gap={cssVar('--spacing-2')}>
          <Text.DefaultSemibold>{t('What syncs:')}</Text.DefaultSemibold>
          <List hasBullets={true}>
            <List.Item>{t('Contact and Household Account (matched by name + email)')}</List.Item>
            <List.Item>
              {t('Opportunity with donation amount, date, and transaction details')}
            </List.Item>
            <List.Item>{t('Payment reference and acknowledgment tracking')}</List.Item>
          </List>
          <Text.DefaultSemibold>{t('How it works:')}</Text.DefaultSemibold>
          <List hasBullets={true}>
            <List.Item>
              {t('Existing donors: New Opportunity added to current Contact/Account')}
            </List.Item>
            <List.Item>
              {t('New donors: New Contact, Household Account, and Opportunity created')}
            </List.Item>
            <List.Item>{t('All donations marked "Closed Won" and "To Be Acknowledged"')}</List.Item>
          </List>
          <Text.DefaultRegular>
            {t('Your Salesforce stays current with every donation—zero manual work required.')}
          </Text.DefaultRegular>
          <Flex pt={cssVar('--spacing-2')}>
            <Checkbox
              attribute={['crm', 'ready_to_enable_integration']}
              label={t("I'm ready to enable the integration.")}
              model={fundraiser}
            />
          </Flex>
        </Flex>
      ),
      nextStepDisabled:
        // If there are validation errors or the checkbox is not checked, disable the next step
        fundraiser.validate(['crm.ready_to_enable_integration'], false).errors?.length > 0 ||
        !fundraiser.get('crm')?.ready_to_enable_integration,
      errors: fundraiser.validate(['crm.ready_to_enable_integration'], false, 'grouped').errors,
      onBack: () => handleOnBackReadyToEnableIntegration(fundraiser),
    },
  ];
}

function getBlackbaudSteps(
  baseProps: Partial<IModalStep>,
  fundraiser: Fundraiser,
  t: TFunction,
): IModalStep[] {
  return [
    {
      ...baseProps,
      title: t("Sync Donations to Raiser's Edge NXT"),
      description: t('Authenticate with Blackbaud to continue.'),
      content: (
        <Flex direction={'column'} gap={cssVar('--spacing-2')}>
          <Text.DefaultSemibold>{t('What syncs:')}</Text.DefaultSemibold>
          <List hasBullets={true}>
            <List.Item>{t('Donation amount, tip, and date')}</List.Item>
            <List.Item>{t('Form name and dedication details')}</List.Item>
            <List.Item>{t('Notes and matching company info')}</List.Item>
          </List>
          <Text.DefaultSemibold>{t('How it works:')}</Text.DefaultSemibold>
          <List hasBullets={true}>
            <List.Item>
              {t('Existing donors (matched by email): Gift added to their Constituent record')}
            </List.Item>
            <List.Item>
              {t('New donors: New Constituent created with complete Gift record')}
            </List.Item>
          </List>
          <Text.DefaultRegular>
            {t("Your donation data flows instantly into Raiser's Edge—no manual entry required.")}
          </Text.DefaultRegular>
          <Flex pt={cssVar('--spacing-2')}>
            <Checkbox
              attribute={['crm', 'ready_to_enable_integration']}
              label={t("I'm ready to enable the integration.")}
              model={fundraiser}
            />
          </Flex>
        </Flex>
      ),
      nextStepDisabled:
        fundraiser.validate(['crm.ready_to_enable_integration'], false).errors?.length > 0,
      onBack: () => handleOnBackReadyToEnableIntegration(fundraiser),
    },
  ];
}

function getCRMConnectSteps({ fundraiser, logos, t }: IProps): IModalStep[] {
  if (!fundraiser) {
    return [];
  }

  const baseProps: Partial<IModalStep> = {
    size: 'default' as TModalSize,
    nextStepDisabled: false,
    completeButtonText: t('Connect'),
  };

  const steps: IModalStep[] = [
    {
      size: 'default',
      title: t('Connect your CRM'),
      description: t('Select your CRM platform and authenticate to begin the integration process.'),
      content: (
        <Radios
          attribute={['crm', '_cls']}
          layout={'block'}
          model={fundraiser}
          options={[
            { name: 'Salesforce', id: 'SalesforceCRM', image: logos.salesforce },
            { name: 'Blackbaud', id: 'BlackbaudCRM', image: logos.blackbaud, disabled: true },
          ]}
          orientation={'horizontal'}
        />
      ),
      nextStepDisabled: fundraiser.validate(['crm._cls'], false).errors?.length > 0,
    },
  ];
  if (fundraiser.get('crm')?._cls === 'SalesforceCRM') {
    steps.push(...getSalesforceSteps(baseProps, fundraiser, t));
  } else if (fundraiser.get('crm')?._cls === 'BlackbaudCRM') {
    steps.push(...getBlackbaudSteps(baseProps, fundraiser, t));
  } else {
    steps.push({
      size: 'default',
      title: '',
      description: '',
      content: <div />,
      nextStepDisabled: true,
    });
  }
  return steps;
}

export default getCRMConnectSteps;
